import React, {useMemo} from "react";
import {Client, isEmpty} from "weed-js";

import {Button} from "@mui/material";
import {Bar, Pie} from "react-chartjs-2";
import {Chart as ChartJS, registerables} from "chart.js";
import {setSyncs, fetchSyncs, fetchSyncScheduledTasks, kickoffSyncs} from "../../redux/models/integrations/Action.js";
import {useSearchParams} from "react-router-dom";

import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {ABORTED} from "../../redux/constants.js";
import {isNullOrUndefinedOrEmpty, formatDate} from "../../utils.js";

import dayjs from "dayjs";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider/LocalizationProvider.js";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs/AdapterDayjs.js";
import {DatePicker} from "@mui/x-date-pickers/DatePicker/DatePicker.js";
import Loading from "../elements/Loading.js";
import {DashboardValue} from "../reports/home/AggregateStats.js";
import {Container, Grid,} from "@mui/material";

import PendingIcon from "@mui/icons-material/HourglassBottom";
import InProgressIcon from "@mui/icons-material/Downloading";
import ErrorIcon from "@mui/icons-material/Error";
import CompleteIcon from "@mui/icons-material/CheckCircle";
import {Table} from "../table/Table";
import {ColumnDef} from "../table/classes/ColumnDef";
import {
    ROUTES_INTEGRATIONS_LEAFLOGIX_DETAIL_BACKFILLS,
    ROUTES_INTEGRATIONS_METRC_DETAIL_FACILITIES
} from "../../utils/Constants";
import {Tooltip} from "@mui/material";
import {DUTCHIE} from "../../constants";


ChartJS.register(...registerables);


export const options = {
    responsive: true,
    plugins: {
        legend: {
            position: "top"
        },
        title: {
            display: true,
            text: "Sync Job Status by Month",
        },
    },
    scales: {
        y: {
            title: {
                text: "Number of Jobs",
                display: true
            }
        },
        x: {
            title: {
                text: "Month",
                display: true
            }
        }
    }
};

const PENDING = 0;
const ENQUEUED = 1;
const STARTED = 2;
const COMPLETE = 3;
const ERRORED = 4;

const statusNames = [PENDING, ENQUEUED, STARTED, COMPLETE, ERRORED];
const statusLabels = {
    [PENDING]: "Pending",
    [ENQUEUED]: "Enqueued",
    [STARTED]: "Started",
    [COMPLETE]: "Complete",
    [ERRORED]: "Errored",
};

// Mui Icons:
const statusBorderColors = {
    [PENDING]: "rgb(0, 0, 0)",
    [ENQUEUED]: "rgb(245, 152, 66)",
    [STARTED]: "rgb(245, 218, 66)",
    [COMPLETE]: "rgb(72, 245, 66)",
    [ERRORED]: "rgb(255, 99, 132)",
};

const statusBackgroundColors = {
    [PENDING]: "rgb(0, 0, 0, .5)",
    [ENQUEUED]: "rgb(245, 152, 66, .5)",
    [STARTED]: "rgb(245, 218, 66, .5)",
    [COMPLETE]: "rgb(0, 245, 0, .5)",
    [ERRORED]: "rgb(255, 0, 0, .5)",
};

const getStatusIcon = (status) => {
    const bgColor = statusBackgroundColors[status];
    const borderColor = statusBorderColors[status];
    if (status === PENDING) {
        return <PendingIcon fill={bgColor} stroke={borderColor} color="inherit" style={{fill: bgColor}}/>;
    } else if (status === ENQUEUED) {
        return <PendingIcon fill={bgColor} stroke={borderColor} color="inherit" style={{fill: bgColor}}/>;
    } else if (status === STARTED) {
        return <InProgressIcon fill={bgColor} stroke={borderColor} color="inherit" style={{fill: bgColor}}/>;
    } else if (status === COMPLETE) {
        return <CompleteIcon fill={bgColor} stroke={borderColor} color="inherit" style={{fill: bgColor}}/>;
    } else if (status === ERRORED) {
        return <ErrorIcon fill={bgColor} stroke={borderColor} color="inherit" style={{fill: bgColor}}/>;
    }
};

const binByStatusAndMonth = (syncs, startDate, endDate) => {
    if (!isNullOrUndefinedOrEmpty(syncs)) {
        return syncs.reduce((bins, sync) => {
            if (isEmpty(sync.tasks)) {
                let taskStart = new Date(sync.start_date);
                let taskEnd = new Date(sync.end_date);
                if (taskStart < startDate || taskEnd > endDate) {
                    return bins;
                }
                let month = taskStart.getMonth() + 1;
                let year = taskStart.getFullYear();
                if (isNullOrUndefinedOrEmpty(bins[year])) {
                    bins[year] = {};
                }
                if (isNullOrUndefinedOrEmpty(bins[year][month])) {
                    bins[year][month] = {};
                }
                let status = sync.status;
                if (isNullOrUndefinedOrEmpty(bins[year][month][status])) {
                    bins[year][month][status] = 0;
                }
                bins[year][month][status] += 1;
                return bins;
            }
            return sync.tasks.reduce((bins, task) => {
                let taskStart = new Date(task.start_date);
                let taskEnd = new Date(task.end_date);
                if (taskStart < startDate || taskEnd > endDate) {
                    return bins;
                }
                let month = taskStart.getMonth() + 1;
                let year = taskStart.getFullYear();
                if (isNullOrUndefinedOrEmpty(bins[year])) {
                    bins[year] = {};
                }
                if (isNullOrUndefinedOrEmpty(bins[year][month])) {
                    bins[year][month] = {};
                }
                let status = taskbotStatusToSyncStatus[task.status];
                if (isNullOrUndefinedOrEmpty(bins[year][month][status])) {
                    bins[year][month][status] = 0;
                }
                bins[year][month][status] += 1;
                return bins;
            }, bins);
        }, {});
    }
    return {};
};

const getTimeseries = (bins, startDate, endDate) => {
    if (isNullOrUndefinedOrEmpty(bins)) {
        return {};
    }
    const timeseries = {date: [], [PENDING]: [], [ENQUEUED]: [], [STARTED]: [], [COMPLETE]: [], [ERRORED]: []};
    let start = new Date(startDate.$d);
    let end = new Date(endDate.$d);
    while (start < end) {
        const year = start.getFullYear();
        const month = start.getMonth() + 1;
        if (!isNullOrUndefinedOrEmpty(bins[year]) && !isNullOrUndefinedOrEmpty(bins[year][month])) {
            const statuses = bins[year][month];
            statusNames.map(statusName => {
                const nJobs = statuses[statusName];
                if (isNullOrUndefinedOrEmpty(nJobs)) {
                    timeseries[statusName].push(0);
                } else {
                    timeseries[statusName].push(nJobs);
                }
            });
        } else {
            statusNames.map(statusName => {
                timeseries[statusName].push(0);
            });
        }
        timeseries.date.push(`${month}/${year}`);
        start.setMonth(start.getMonth() + 1);
    }
    return timeseries;
};


const getPieChartData = (timeseries) => {
    const pending = (timeseries[PENDING] || []).reduce((total, current) => {
        return total + current;
    }, 0);
    const enqueued = (timeseries[ENQUEUED] || []).reduce((total, current) => {
        return total + current;
    }, 0);
    const started = (timeseries[STARTED] || []).reduce((total, current) => {
        return total + current;
    }, 0);
    const complete = (timeseries[COMPLETE] || []).reduce((total, current) => {
        return total + current;
    }, 0);
    const errored = (timeseries[ERRORED] || []).reduce((total, current) => {
        return total + current;
    }, 0);

    const borderColor = [
        "rgb(0, 0, 0)",
        "rgb(245, 152, 66)",
        "rgb(245, 218, 66)",
        "rgb(72, 245, 66)",
        "rgb(255, 99, 132)",
    ];

    const backgroundColor = [
        "rgb(0, 0, 0, .5)",
        "rgb(245, 152, 66, .5)",
        "rgb(245, 218, 66, .5)",
        "rgb(0, 245, 0, .5)",
        "rgb(255, 0, 0, .5)",
    ];

    return {
        labels: ["Pending", "Enqueued", "Started", "Complete", "Errored"],
        datasets: [{
            data: [pending, enqueued, started, complete, errored],
            backgroundColor: backgroundColor,
            borderColor: borderColor,
        }],
    };
};


const getBarChartData = (timeseries) => {
    return {
        labels: timeseries.date,
        datasets: [
            {
                label: "Pending",
                data: (timeseries[PENDING] || []).map((nJobs, idx) => {
                    return {x: idx, y: nJobs};
                }),
                borderColor: "rgb(0, 0, 0))",
                backgroundColor: "rgb(0, 0, 0, .5)",
            },
            {
                label: "Enqueued",
                data: (timeseries[ENQUEUED] || []).map((nJobs, idx) => {
                    return {x: idx, y: nJobs};
                }),
                borderColor: "rgb(245, 152, 66)",
                backgroundColor: "rgb(245, 152, 66, .5)",
            },
            {
                label: "Started",
                data: (timeseries[STARTED] || []).map((nJobs, idx) => {
                    return {x: idx, y: nJobs};
                }),
                borderColor: "rgb(245, 218, 66)",
                backgroundColor: "rgb(245, 218, 66, .5)",
            },
            {
                label: "Complete",
                data: (timeseries[COMPLETE] || []).map((nJobs, idx) => {
                    return {x: idx, y: nJobs};
                }),
                borderColor: "rgb(72, 245, 66)",
                backgroundColor: "rgb(0, 245, 0, .5)",
            },
            {
                label: "Errored",
                data: (timeseries[ERRORED] || []).map((nJobs, idx) => {
                    return {x: idx, y: nJobs};
                }),
                borderColor: "rgb(255, 99, 132)",
                backgroundColor: "rgb(255, 0, 0, .5)",
            },
        ],
    };

};

const taskbotStatusToSyncStatus = {
    "scheduled": ENQUEUED,
    "running": STARTED,
    "errored": ERRORED,
    "succeeded": COMPLETE,
    "expired": ERRORED,
    "timed out": ERRORED,
    "orphaned": ERRORED,
    "retrying": PENDING,
    "dropped": ERRORED,
    "permanently_errored": ERRORED,
    "rescheduled": ENQUEUED
};

const getTableOptions = (syncs) => {
    // TODO: Set id="sync-table" on the table element
    // TODO: Set className = "-striped -highlight mb-3"
    const tableHeaders = [
        new ColumnDef({displayName: "Integration"}),
        new ColumnDef({displayName: "Job"}),
        new ColumnDef({displayName: "Facility"}),
        new ColumnDef({displayName: "Sync Start Date"}),
        new ColumnDef({displayName: "Sync End Date"}),
        new ColumnDef({displayName: "Status"}),
        new ColumnDef({displayName: "Last Updated"})
    ];

    const data = syncs.reduce((acc, sync) => {
        const startDate = new Date(sync.start_date);
        const formattedStartDate = `${startDate.getMonth() + 1}/${startDate.getDate()}/${startDate.getFullYear()}`;
        const endDate = new Date(sync.end_date);
        const formattedEndDate = `${endDate.getMonth() + 1}/${endDate.getDate()}/${endDate.getFullYear()}`;

        if (sync.tasks.length === 0) {
            const icon = getStatusIcon(sync.status);
            const label = statusLabels[sync.status];
            acc.push([
                sync.namespace,
                sync.name,
                sync.facility_license_number || "N/A",
                formattedStartDate,
                formattedEndDate,
                <div key={"sync-" + sync.id + "-task-0"}>{icon}{label}</div>,
                new Date(sync.updated_at).toLocaleString()
            ]);
            return acc;
        }
        acc = acc.concat(sync.tasks.map((task => {
            const status = taskbotStatusToSyncStatus[task.status];
            const icon = getStatusIcon(status);
            const label = statusLabels[status];
            const errorMessage = task.exception || "";  // This field only appears for superusers.

            return [
                sync.namespace,
                sync.name,
                sync.facility_license_number || task.facility_license_number,
                formattedStartDate,
                formattedEndDate,
                isEmpty(errorMessage)
                    ? <div key={"sync-" + sync.id + "-task-" + task.id}>{icon}{label}</div>
                    : <Tooltip key={"sync-" + sync.id + "-task-" + task.id} title={errorMessage}>
                        {icon}{label}
                    </Tooltip>,
                new Date(task.updated_at || sync.updated_at).toLocaleString()
            ];
        })));

        return acc;
    }, []);

    return {
        columnDefs: tableHeaders,
        data: data
    };
};


const SyncStatus = ({selectedOrganization, integrationAccountId, integrationNamespace, startDate, endDate}) => {
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);
    const [syncs, setSyncs] = useState([]);

    const [nRefreshes, setNRefreshes] = useState(0);
    const maxRefreshes = 20;

    useEffect(() => {
        if (!integrationAccountId || isNullOrUndefinedOrEmpty(selectedOrganization) || !selectedOrganization.id) {
            return;
        }
        const abortController = new AbortController();
        let refreshTimeout = setTimeout(() => {
            if (nRefreshes < maxRefreshes) {
                setNRefreshes(nRefreshes + 1);
            }
        }, 30000);
        setLoading(true);
        dispatch(fetchSyncs({
            organization_id: selectedOrganization.id,
            namespace: integrationNamespace,
            start_date__gte: formatDate(startDate),
            start_date__lte: formatDate(endDate),
            limit: 50,
        }, abortController)).then((fetchedSyncs) => {
            console.log("Got fetched syncs: ", fetchedSyncs);
            const promises = fetchedSyncs.map(sync => dispatch(fetchSyncScheduledTasks(sync.id, abortController)));
            console.log("Got promises: ", promises);
            if (isEmpty(promises)) {
                setSyncs(fetchedSyncs);
            } else {
                Promise.all(promises).then((taskss) => {
                    console.log("Got tasks: ", taskss);
                    fetchedSyncs.forEach(sync => {
                        if (isEmpty(sync.tasks)) {
                            console.log("Adding tasks for sync ", sync.id, taskss);
                            sync.tasks = taskss.find(ts => {
                                if (isEmpty(ts) || isEmpty(ts[0])) {
                                    return false;
                                } else if (ts[0].backfill_id === sync.id) {
                                    return true;
                                }
                                return false;
                            }) || [];
                        }
                    });
                    setSyncs(fetchedSyncs);
                });
            }
            setLoading(false);
        }).catch(err => {
            if (err !== ABORTED) {
                console.error(err);
            }
            setLoading(false);
        });
        return () => {
            abortController.abort();
            clearTimeout(refreshTimeout);
        };
    }, [integrationNamespace, integrationAccountId, startDate, endDate, nRefreshes]);

    if (loading) {
        return <Loading title={"Updating status..."}/>;
    }

    if (isNullOrUndefinedOrEmpty(syncs)) {
        return <p>You do not have any syncs in progress. You can start one on the &quot;Facilities&quot; tab.</p>;
    }


    const timeseries = getTimeseries(binByStatusAndMonth(syncs, startDate, endDate), startDate, endDate);
    const totalTasks = syncs.length;
    const completeTasks = (syncs || []).reduce((nComplete, sync) => {
        if (isNullOrUndefinedOrEmpty(sync.tasks)) {
            return nComplete;
        }
        return nComplete + sync.tasks.reduce((innerNComplete, task) => {
            if (isNullOrUndefinedOrEmpty(task.status)) {
                return innerNComplete;
            }
            if (taskbotStatusToSyncStatus[task.status] === COMPLETE) {
                return innerNComplete + 1;
            }
            return innerNComplete;
        }, 0);
    }, 0);

    const percentComplete = `${Math.round(completeTasks / totalTasks * 100)}%`;
    const barChartData = getBarChartData(timeseries);
    const pieChartData = getPieChartData(timeseries);

    console.log("Got syncs: ", syncs);

    return <Container className={"sync-progress dashboard"}>
        <Grid container spacing={12} style={{alignItems: "center"}}>
            <Grid item xs={12} md={6}>
                <DashboardValue title={"for these dates"} content={percentComplete} units={"Complete"}/>
            </Grid>
            <Grid item xs={0} sm={0} md={6}>
                <Pie data={pieChartData}/>
            </Grid>
            <Grid item xs={0} sm={0} md={6}>
                <Bar options={options} data={barChartData}/>
            </Grid>
        </Grid>
        <div>
            <p>The table below gives the status of each incomplete job dispatched to run your sync. If you&apos;re
                seeing errors and want to file a bug report, please include at least one &quot;errored&quot; Job ID in your
                request.</p>
            <div><Table {...getTableOptions(syncs)}/></div>
        </div>
    </Container>;
};

export const Syncs = ({selectedOrganization, integrationAccountId, integrationNamespace, loggedInUser}) => {

    const [params, setParams] = useSearchParams();
    const today = new Date();
    const _tomorrow = new Date();
    const tomorrow = _tomorrow.setTime(_tomorrow.getTime() + 24 * 60 * 60 * 1000);
    const lookback = (new Date()).setMonth(today.getMonth() - 3);
    const defaultStart = params.get("startDate") ? dayjs(params.get("startDate")) : dayjs(lookback);
    const defaultEnd = params.get("endDate") ? dayjs(params.get("endDate")) : dayjs(tomorrow);
    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(defaultEnd);
    const dispatch = useDispatch();
    const client = new Client();
    const [etls, setEtls] = useState([]);

    const route = integrationNamespace === "metrc" ? ROUTES_INTEGRATIONS_METRC_DETAIL_FACILITIES : ROUTES_INTEGRATIONS_LEAFLOGIX_DETAIL_BACKFILLS;
    const syncLink = <a href={route.replace(":id", integrationAccountId)}>Manage Facility Quota and Data Syncs</a>;

    useEffect(() => {
        const abortController = new AbortController();
        client.sso.dal.v1().get("/etls/", {params: {namespace: integrationNamespace}}, abortController).then((response) => {
            setEtls(response?.data?.results || []);
        }).catch((error) => {
            console.error("Error fetching ETLs: ", error);
            return [];
        });
        return () => abortController.abort();
    }, [integrationNamespace]);

    return <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Container className={"chart dashboard"}>
            <Container className={"chart-description"}>
                <Grid container spacing={12}>
                    <Grid item xs={12}>
                        <p>Syncs allow you to pull in historical data. When you change a token or add a new location
                            you&apos;ll want to run a sync over the last 90 days. You can do that in your Facilities tab
                            under {syncLink}.</p>
                        <p>If you have problems please reach out via our <a
                            href="https://canold.atlassian.net/servicedesk/customer/portal/4">support desk</a> by
                            submitting a request for technical support. We&apos;ll get back to you typically within a
                            few hours.</p>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container spacing={12} className="sync-date-range">
                            <Grid item xs={12} md={4}>
                                <DatePicker style={{width: "100%"}} label="Start Date" value={dayjs(startDate)}
                                    onChange={(newDate) => {
                                        setStartDate(dayjs(newDate));
                                        params.set("startDate", formatDate(dayjs(newDate)));
                                        setParams(params);
                                    }}/>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <DatePicker label="End Date" value={dayjs(endDate)} onChange={(newDate) => {
                                    setEndDate(dayjs(newDate));
                                    params.set("endDate", formatDate(dayjs(newDate)));
                                    setParams(params);
                                }}/>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                {integrationNamespace === DUTCHIE && <Button variant="contained" onClick={() => {
                                    etls.map(etl => {
                                        dispatch(kickoffSyncs({
                                            name: etl.name,
                                            organization_id: selectedOrganization.id,
                                            integration_account_id: integrationAccountId,
                                            namespace: integrationNamespace,
                                            start_date: formatDate(startDate),
                                            end_date: formatDate(endDate),
                                            updated_at: new Date().toISOString(),
                                            owner_id: loggedInUser.id,
                                            etl_id: etl.id
                                        })).then(update => {
                                            console.log("Update", update);
                                        }).catch(err => {
                                            if (err === ABORTED) {
                                                return;
                                            }
                                            console.error(err);
                                        });
                                    });
                                }}>Kickoff Backfills</Button>}
                            </Grid>
                            <Grid item xs={12}>
                                <SyncStatus
                                    selectedOrganization={selectedOrganization}
                                    integrationAccountId={integrationAccountId}
                                    integrationNamespace={integrationNamespace}
                                    startDate={startDate} endDate={endDate}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Container>
        </Container>
    </LocalizationProvider>
    ;
};
